import { createApp } from 'vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from '../shared/helpers/HTMLSanitizer';
import { directive as onClickaway } from 'vue3-click-away';
import { isSameHost } from '@chatwoot/utils';

import slugifyWithCounter from '@sindresorhus/slugify';
import PublicArticleSearch from './components/PublicArticleSearch.vue';
import TableOfContents from './components/TableOfContents.vue';
import SidebarThemeToggle from './components/SidebarThemeToggle.vue';
import { initializeTheme } from './portalThemeHelper.js';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages.js';

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mountedApps = [];
let externalLinksBound = false;
let localeSwitcherBound = false;
let detailsClickAwayBound = false;

const resolveLocaleHomeUrl = (selectedLocale, portalSlug) => {
  const baseUrl = String(window.portalConfig?.helpCenterBaseUrl || '').trim().replace(/\/$/, '');
  const targetPath = `/${encodeURIComponent(selectedLocale)}/${encodeURIComponent(portalSlug)}/`;

  return baseUrl ? `${baseUrl}${targetPath}` : targetPath;
};

const resolveLocalizedTarget = selectedLocale => {
  const localizedUrls = window.portalConfig?.localizedUrls;
  if (!localizedUrls || typeof localizedUrls !== 'object') {
    return null;
  }

  return localizedUrls[selectedLocale] || null;
};

const resolvePortalLocaleCodes = () => {
  const localizedUrls = window.portalConfig?.localizedUrls;
  if (localizedUrls && typeof localizedUrls === 'object') {
    const localeCodes = Object.keys(localizedUrls)
      .map(locale => locale.trim().toLowerCase())
      .filter(Boolean);
    if (localeCodes.length) {
      return [...new Set(localeCodes)];
    }
  }

  const currentLocale = String(window.portalConfig?.localeCode || '')
    .trim()
    .toLowerCase();
  return currentLocale ? [currentLocale] : [];
};

// Тримаємо guard у JS-модулі, а не в DOM, бо Turbo може замінити body
// або повернути сторінку з кешу зі старим data-атрибутом. Event delegation на
// document лишається стабільним для нового header без повного reload сторінки.
let hrSolutionsDropdownListenersBound = false;

const closestFromEventTarget = (target, selector) => {
  if (target?.closest) {
    return target.closest(selector);
  }

  return target?.parentElement?.closest(selector) || null;
};

const setHrSolutionsDropdownState = (dropdown, shouldOpen) => {
  if (!dropdown) return;

  const menu = dropdown.querySelector('.hr-solutions-menu');
  const toggle = dropdown.querySelector('.hr-solutions-toggle');
  const chevron = dropdown.querySelector('.hr-solutions-chevron');
  if (!menu || !toggle) return;

  menu.classList.toggle('hidden', !shouldOpen);
  menu.classList.toggle('flex', shouldOpen);
  toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  chevron?.classList.toggle('rotate-180', shouldOpen);
};

const closeAllHrSolutionsDropdowns = exceptDropdown => {
  document.querySelectorAll('.hr-solutions-dropdown').forEach(dropdown => {
    if (exceptDropdown && dropdown === exceptDropdown) return;
    setHrSolutionsDropdownState(dropdown, false);
  });
};

const initializeHrSolutionsDropdowns = () => {
  if (typeof document === 'undefined' || hrSolutionsDropdownListenersBound) {
    return;
  }

  hrSolutionsDropdownListenersBound = true;

  document.addEventListener('click', event => {
    const toggle = closestFromEventTarget(event.target, '.hr-solutions-toggle');
    if (toggle) {
      event.preventDefault();
      const dropdown = toggle.closest('.hr-solutions-dropdown');
      const menu = dropdown?.querySelector('.hr-solutions-menu');
      const shouldOpen = Boolean(menu?.classList.contains('hidden'));

      closeAllHrSolutionsDropdowns(dropdown);
      setHrSolutionsDropdownState(dropdown, shouldOpen);
      return;
    }

    if (!closestFromEventTarget(event.target, '.hr-solutions-dropdown')) {
      closeAllHrSolutionsDropdowns();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      closeAllHrSolutionsDropdowns();
    }
  });

  document.addEventListener('turbo:before-cache', () => {
    closeAllHrSolutionsDropdowns();
  });
};

export const getHeadingsfromTheArticle = () => {
  const rows = [];
  const articleElement = document.getElementById('cw-article-content');
  articleElement.querySelectorAll('h1, h2, h3').forEach(element => {
    const headingText = element.innerText;
    const slug = slugifyWithCounter(headingText);
    element.id = slug;
    element.className = 'scroll-mt-24 heading';

    const permalink = document.createElement('a');
    permalink.className = 'permalink text-slate-600 ml-3';
    permalink.href = `#${slug}`;
    permalink.title = headingText;
    permalink.dataset.turbo = 'false';
    permalink.textContent = '#';
    element.appendChild(permalink);
    rows.push({
      slug,
      title: headingText,
      tag: element.tagName.toLowerCase(),
    });
  });
  return rows;
};

export const openExternalLinksInNewTab = () => {
  if (externalLinksBound) return;
  externalLinksBound = true;

  document.addEventListener('click', event => {
    if (!document.querySelector('#cw-article-content')) return;
    const { customDomain, hostURL } = window.portalConfig;

    const link = event.target.closest('a');

    if (link) {
      const currentLocation = window.location.href;
      const linkHref = link.href;

      // Check against current location and custom domains
      const isInternalLink =
        isSameHost(linkHref, currentLocation) ||
        (customDomain && isSameHost(linkHref, customDomain)) ||
        (hostURL && isSameHost(linkHref, hostURL));

      if (!isInternalLink) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer'; // Security and performance benefits
        // Prevent default if you want to stop the link from opening in the current tab
        event.stopPropagation();
      }
    }
  });
};

export const InitializationHelpers = {
  beforeRender: () => {
    mountedApps.splice(0).forEach(app => app.unmount());
  },
  navigateToLocalePage: () => {
    if (localeSwitcherBound) return;
    localeSwitcherBound = true;
    document.addEventListener('change', e => {
      const localeSwitcher = e.target.closest('.locale-switcher');
      if (!localeSwitcher) return;

      const { portalSlug } = localeSwitcher.dataset;
      const selectedLocale = localeSwitcher.value;
      const localizedTarget = resolveLocalizedTarget(selectedLocale);

      if (localizedTarget) {
        window.location.href = localizedTarget;
        return;
      }

      window.location.href = resolveLocaleHomeUrl(selectedLocale, portalSlug);
    });
  },

  initializeSearch: () => {
    ['#search-wrap', '#search-wrap-hero'].forEach(selector => {
      const mountPoint = document.querySelector(selector);
      if (!mountPoint) return;
      const size = mountPoint.dataset.size || 'default';
      const showKbd = !!mountPoint.dataset.kbd;
      // eslint-disable-next-line vue/one-component-per-file
      const app = createApp({
        components: { PublicArticleSearch },
        data() {
          return { size, showKbd };
        },
        template: '<PublicArticleSearch :size="size" :show-kbd="showKbd" />',
      });
      app.use(VueDOMPurifyHTML, domPurifyConfig);
      app.directive('on-clickaway', onClickaway);
      app.mount(selector);
      mountedApps.push(app);
    });
  },

  initializeTableOfContents: () => {
    const isOnArticlePage = document.querySelector('#cw-hc-toc');
    if (isOnArticlePage) {
      // eslint-disable-next-line vue/one-component-per-file
      const app = createApp({
        components: { TableOfContents },
        data() {
          return { rows: getHeadingsfromTheArticle() };
        },
        template: '<table-of-contents :rows="rows" />',
      });

      app.use(VueDOMPurifyHTML, domPurifyConfig);
      app.mount('#cw-hc-toc');
      mountedApps.push(app);
    }
  },

  initializeSidebarThemeToggle: () => {
    const mountPoint = document.querySelector('#sidebar-theme-toggle');
    if (mountPoint) {
      // eslint-disable-next-line vue/one-component-per-file
      const app = createApp({
        components: { SidebarThemeToggle },
        template: '<sidebar-theme-toggle />',
      });
      app.directive('on-clickaway', onClickaway);
      app.mount('#sidebar-theme-toggle');
      mountedApps.push(app);
    }
  },

  initializeDetailsClickAway: () => {
    if (detailsClickAwayBound) return;
    detailsClickAwayBound = true;
    document.addEventListener('click', event => {
      document
        .querySelectorAll('details[data-close-on-clickaway][open]')
        .forEach(details => {
          if (!details.contains(event.target)) {
            details.removeAttribute('open');
          }
        });
    });
  },

  appendPlainParamToURLs: () => {
    const portalSlug = String(window.portalConfig?.portalSlug || '').trim();
    const localeCodes = resolvePortalLocaleCodes();
    const localePattern = localeCodes.length
      ? `(?:${localeCodes.map(escapeRegExp).join('|')})`
      : '[^/]+';
    const localeScopedPortalPath = portalSlug
      ? new RegExp(`^/${localePattern}/${escapeRegExp(portalSlug)}(?:/|$)`, 'i')
      : null;

    [...document.getElementsByTagName('a')].forEach(aTagElement => {
      if (!aTagElement.href) {
        return;
      }

      const url = new URL(aTagElement.href, window.location.origin);
      const isLegacyPortalPath = portalSlug ? url.pathname.includes(`/hc/${portalSlug}/`) : false;
      const isLocalizedPortalPath = localeScopedPortalPath ? localeScopedPortalPath.test(url.pathname) : false;

      if (!isLegacyPortalPath && !isLocalizedPortalPath) {
        return;
      }

      url.searchParams.set('show_plain_layout', 'true');
      aTagElement.setAttribute('href', url.toString());
    });
  },

  setDirectionAttribute: () => {
    const htmlElement = document.querySelector('html');
    // If direction is already applied through props, do not apply again (iframe case)
    const hasDirApplied = htmlElement.getAttribute('data-dir-applied');
    if (!htmlElement || hasDirApplied) return;

    const localeFromHtml = htmlElement.lang;
    htmlElement.dir =
      localeFromHtml && getLanguageDirection(localeFromHtml) ? 'rtl' : 'ltr';
  },

  initializeThemesInPortal: initializeTheme,

  initialize: () => {
    initializeHrSolutionsDropdowns();
    openExternalLinksInNewTab();
    InitializationHelpers.setDirectionAttribute();
    if (window.portalConfig.isPlainLayoutEnabled === 'true') {
      InitializationHelpers.appendPlainParamToURLs();
    } else {
      InitializationHelpers.initializeThemesInPortal();
      InitializationHelpers.navigateToLocalePage();
      InitializationHelpers.initializeSearch();
      InitializationHelpers.initializeTableOfContents();
      InitializationHelpers.initializeSidebarThemeToggle();
      InitializationHelpers.initializeDetailsClickAway();
    }
  },

  onLoad: () => {
    InitializationHelpers.initialize();
    if (window.location.hash) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      const a = document.createElement('a');
      a.href = window.location.hash;
      a['data-turbo'] = false;
      a.click();
    }
  },
};
